// CAMINHO: src/components/EventFormModal.tsx
// VERSÃO: FINAL E INTELIGENTE

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";

// A interface para um processo, simplificada para este componente
interface CaseInfo {
  id: string;
  title: string;
}

// As props que o nosso modal vai receber da página Cases.tsx
interface EventFormModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  caseId?: string | null; // ID do processo que já vem selecionado
  onSaveSuccess?: () => void;
}

export function EventFormModal({ isOpen, onOpenChange, caseId, onSaveSuccess }: EventFormModalProps) {
  const { toast } = useToast();
  const [selectedCase, setSelectedCase] = useState<CaseInfo | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    responsible: '',
    case_id: ''
  });

  // Este efeito roda sempre que o modal abre ou o processo selecionado muda
  useEffect(() => {
    // Se o modal não está abrindo, limpa o formulário para a próxima vez
    if (!isOpen) {
      setFormData({ title: '', start_time: '', end_time: '', location: '', description: '', responsible: '', case_id: '' });
      setSelectedCase(null);
      return;
    }

    // Se o modal abriu com um ID de processo já definido...
    if (caseId) {
      const fetchCaseDetails = async () => {
        const { data, error } = await supabase
          .from('cases')
          .select('id, title')
          .eq('id', caseId)
          .single();
        
        if (data) {
          setSelectedCase(data);
          setFormData(prev => ({ ...prev, case_id: data.id }));
        }
      };
      fetchCaseDetails();
    }
  }, [isOpen, caseId]);


  const handleSave = async () => {
    if (!formData.title || !formData.start_time) {
        toast({ title: "Campos Obrigatórios", description: "O título e a data de início do evento são necessários.", variant: "destructive" });
        return;
    }

    // Garante que o case_id seja nulo se não houver um, para o banco de dados aceitar
    const eventToSave = {
      ...formData,
      case_id: formData.case_id || null
    };

    // TODO: Create events table in database
    console.log('Event data:', eventToSave);
    toast({ title: "Evento", description: "Funcionalidade temporariamente desabilitada. Tabela 'events' precisa ser criada no banco.", variant: "destructive" });
    onOpenChange(false);
    return;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(95vw,640px)] max-w-[640px] rounded-3xl p-6 sm:p-8">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Evento</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Input
            placeholder="Título do Evento (Ex: Audiência de Instrução)"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="start_time">Início</Label>
              <Input
                id="start_time"
                type="datetime-local"
                value={formData.start_time}
                onChange={e => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="end_time">Fim</Label>
              <Input
                id="end_time"
                type="datetime-local"
                value={formData.end_time}
                onChange={e => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
          </div>
          <Input
            placeholder="Local (Ex: Sala de audiência 1)"
            value={formData.location}
            onChange={e => setFormData({ ...formData, location: e.target.value })}
          />
          <Input
            placeholder="Responsável"
            value={formData.responsible}
            onChange={e => setFormData({ ...formData, responsible: e.target.value })}
          />
          <Textarea
            placeholder="Descrição e detalhes sobre o evento..."
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            className="min-h-[120px]"
          />
          <div className="grid gap-2">
            <Label>Processo Vinculado</Label>
            <Input
              value={selectedCase ? `${selectedCase.title}` : 'Nenhum processo vinculado'}
              disabled
            />
          </div>
          <Button className="w-full" onClick={handleSave}>Salvar Evento</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}